import {
  type CollectionResourceLinkage,
  type NullResourceLinkage,
  type SingleResourceLinkage,
} from '../ResourceLinkage'

/**
 * Possible id inputs for a JSON:API relationship.
 */
type IdOrIds = string | Array<string> | null | undefined

/**
 * Builds a resource linkage object for use in a JSON:API relationship PATCH body.
 * Intended to be spread into the `relationships` object.
 *
 * @example
 *   const jsonApiRequestBody = {
 *    relationships: {
 *      ...buildRelationshipForUpdate({
 *        resourceType: 'category',
 *        idOrIds: params.swimlaneId,
 *      })
 *    }
 *   }
 */
export const buildRelationshipForUpdate = <
  const TKey extends string,
  const TType extends string,
>({
  relationshipKey,
  resourceType,
  idOrIds,
}: {
  /**
   * Key used for the relationship name. Defaults to the `resourceType` if not provided.
   */
  relationshipKey?: TKey
  /**
   * Key used to represent the `type` property in the resource linkage object.
   */
  resourceType: TType
  /**
   * One or more resource ids to be used to create the resource linkage.
   *
   * If `undefined`, no object will be returned so that it can safely be
   * spread into update request bodies without causing unwanted updates.
   *
   * If `null`, the resource will be assumed to be a nullable to-one relationship with
   * no related resource.
   */
  idOrIds: IdOrIds
}):
  | undefined
  | Record<
      string,
      | NullResourceLinkage
      | SingleResourceLinkage<TType>
      | CollectionResourceLinkage<TType>
    > => {
  if (idOrIds === undefined) return undefined

  const relationshipProperty = relationshipKey ?? resourceType

  if (Array.isArray(idOrIds)) {
    return {
      [relationshipProperty]: {
        data: idOrIds.map((id) => ({ id, type: resourceType })),
      },
    }
  }

  if (idOrIds === null) {
    return { [relationshipProperty]: { data: null } }
  }

  if (typeof idOrIds === 'string') {
    return {
      [relationshipProperty]: {
        data: { id: idOrIds, type: resourceType },
      },
    }
  }

  throw new Error(
    `Unhandled case for buildRelationshipForUpdate. resourceType: ${resourceType}, idOrIds: ${idOrIds}`
  )
}
